<template>
  <div>
    <div :class="$style.overlay"></div>
    <div :class="$style.paper">
      <button :class="$style.closeButton" @click="$router.back()" aria-label="닫기">
        ×
      </button>

      <img :class="$style.logo" alt="Linkook logo" src="@/assets/images/linkooklive_longlogo.png" />

      <h5 :class="$style.title">구매 폼 만들기</h5>

      <div v-if="loading" :class="$style.loading">로딩 중...</div>
      <div v-else-if="error" :class="$style.error">{{ error }}</div>
      <div v-else :class="$style.inputs">
        <form v-if="commissionId" @submit.prevent="saveFormInfo" :class="$style.formContainer">

          <!-- 기본 안내사항 섹션 -->
          <section :class="$style.section">
            <div :class="$style.sectionHeader">
              <h2 :class="$style.sectionTitle">기본 안내사항</h2>
              <p :class="$style.sectionDescription">예매자에게 보여질 안내 정보를 작성하세요</p>
              <div :class="$style.autoIncludeNotice">
                <span :class="$style.autoIncludeIcon">✓</span>
                <div :class="$style.autoIncludeContent">
                  <span :class="$style.autoIncludeLabel">자동 포함</span>
                  <p :class="$style.autoIncludeText">커미션 기본정보와 입금정보는 구매 폼에 별도 입력 없이 자동으로 포함됩니다</p>
                </div>
              </div>
            </div>

            <!-- 각 안내사항 카드 -->
            <div v-for="(notice, index) in noticeFields" :key="notice.id" :class="$style.questionCard">
              <div :class="$style.questionHeader">
                <span :class="$style.questionNumber">안내 {{ index + 1 }}</span>
                <div :class="$style.headerActions">
                  <div :class="$style.reorderControls">
                    <button type="button" :class="$style.reorderButton" @click="moveNotice(index, -1)"
                      :disabled="saving || index === 0" aria-label="위로">
                      ↑
                    </button>
                    <button type="button" :class="$style.reorderButton" @click="moveNotice(index, 1)"
                      :disabled="saving || index === noticeFields.length - 1" aria-label="아래로">
                      ↓
                    </button>
                  </div>
                  <button type="button" @click="removeNotice(index)" :class="$style.deleteButton" :disabled="saving">
                    삭제
                  </button>
                </div>
              </div>

              <div :class="$style.input">
                <h4 :class="$style.required">제목</h4>
                <input :class="$style.textInput" v-model="notice.title" placeholder="예: 입장 안내, 문의 및 환불 요청, 촬영 안내"
                  :disabled="saving" />
              </div>

              <div :class="$style.input">
                <h4 :class="$style.required">내용</h4>
                <textarea :class="$style.textarea" v-model="notice.content" rows="6"
                  placeholder="• 문의·환불은 공연 3일 전까지 신청해 주세요&#10;• 입장은 공연 30분 전부터 가능합니다&#10;• 현장에서는 성함 확인 후 입장권을 배부합니다"
                  :disabled="saving"></textarea>
              </div>
            </div>

            <!-- 안내사항 추가 버튼 -->
            <button type="button" @click="addNotice" :class="$style.addFieldButton" :disabled="saving">
              + 안내사항 추가
            </button>
          </section>

          <!-- 취소/환불 규정 섹션 -->
          <section :class="$style.section">
            <div :class="$style.sectionHeader">
              <h2 :class="$style.sectionTitle">취소/환불 규정</h2>
              <p :class="$style.sectionDescription">별도 규정을 작성하지 않으면 링꾹라이브 기본 규정이 자동으로 안내됩니다</p>
            </div>

            <!-- 기본 규정 안내 (커스텀 미사용 시 항상 표시) -->
            <div :class="$style.defaultPolicyNotice">
              <div :class="$style.defaultPolicyHeader">
                <span :class="$style.defaultPolicyBadge">기본 규정</span>
                <span :class="$style.defaultPolicyLabel">링꾹라이브 취소/환불 규정 기준</span>
              </div>
              <ul :class="$style.defaultPolicyList">
                <li>공연 시작일 3일 전까지: 결제 금액 전액 환불</li>
                <li>공연 시작일 2일 전: 결제 금액의 70% 환불</li>
                <li>공연 시작일 1일 전 및 당일 이후: 환불 불가</li>
              </ul>
              <p :class="$style.defaultPolicyNote">
                직접 작성한 규정이 있을 경우, 해당 규정이 위 기본 규정보다 우선 적용됩니다.
              </p>
            </div>

            <div :class="$style.cancelPolicyToggle">
              <label :class="$style.toggleLabel">
                <span :class="$style.toggleText">별도 규정 직접 작성하기 <span :class="$style.toggleOptional">(선택)</span></span>
                <div :class="[$style.toggleSwitch, cancelPolicy.enabled ? $style.toggleSwitchOn : '']"
                  @click="!saving && (cancelPolicy.enabled = !cancelPolicy.enabled)">
                  <div :class="$style.toggleKnob"></div>
                </div>
              </label>
            </div>

            <div v-if="cancelPolicy.enabled" :class="$style.cancelPolicyContent">
              <div :class="$style.input">
                <h4 :class="$style.required">규정 내용</h4>
                <textarea :class="$style.textarea" v-model="cancelPolicy.content" rows="8"
                  placeholder="예시:&#10;• 공연 시작일 3일 전까지: 결제 금액 전액 환불&#10;• 공연 시작일 2일 전: 결제 금액의 70% 환불&#10;• 공연 시작일 1일 전 및 당일 이후: 환불 불가&#10;• 취소/환불 요청은 마이페이지 예매내역에서 가능합니다&#10;&#10;※ 위 규정은 링꾹라이브 이용약관(4조)에 따른 기본 규정이며, 주최자가 별도 규정을 명시한 경우 해당 규정이 우선 적용됩니다."
                  :disabled="saving"></textarea>
              </div>
            </div>
          </section>

          <!-- 티켓 패키지 구성 섹션 -->
          <section :class="$style.section">
            <div :class="$style.sectionHeader">
              <h2 :class="$style.sectionTitle"> 티켓 패키지 구성</h2>
              <p :class="$style.sectionDescription"> 가격별 티켓 패키지 구성품 세트를 만들어보세요 (선택사항)</p>
            </div>

            <div v-for="(tier, tIdx) in rewardTiers" :key="tier.id" :class="$style.questionCard">
              <div :class="$style.questionHeader">
                <span :class="$style.questionNumber">패키지 {{ tIdx + 1 }}</span>
                <div :class="$style.headerActions">
                  <div :class="$style.reorderControls">
                    <button type="button" :class="$style.reorderButton" @click="moveRewardTier(tIdx, -1)"
                      :disabled="saving || tIdx === 0" aria-label="위로">↑</button>
                    <button type="button" :class="$style.reorderButton" @click="moveRewardTier(tIdx, 1)"
                      :disabled="saving || tIdx === rewardTiers.length - 1" aria-label="아래로">↓</button>
                  </div>
                  <button type="button" @click="removeRewardTier(tIdx)" :class="$style.deleteButton" :disabled="saving">
                    삭제
                  </button>
                </div>
              </div>

              <div :class="$style.input">
                <h4 :class="$style.required">패키지명</h4>
                <input :class="$style.textInput" v-model="tier.name" placeholder="예: 기본 패키지, 프리미엄 패키지"
                  :disabled="saving" />
              </div>

              <div :class="$style.input">
                <h4 :class="$style.required">가격 (원)</h4>
                <input :class="$style.textInput" v-model.number="tier.price" type="number" min="0" placeholder="50000"
                  :disabled="saving" />
              </div>

              <div :class="$style.input">
                <h4>설명 (선택)</h4>
                <input :class="$style.textInput" v-model="tier.description" placeholder="패키지에 대한 간단한 설명"
                  :disabled="saving" />
              </div>

              <!-- 이미지 업로드 -->
              <div :class="$style.input">
                <h4>이미지 <span style="font-size: 12px; color: #999;">(선택, 최대 10개)</span></h4>
                <div :class="$style.imageGridArea">
                  <div v-for="(img, imgIdx) in tier.images" :key="imgIdx" :class="$style.imagePreviewWrapper">
                    <img :src="img.previewUrl || img.url" :class="$style.imagePreview" alt="패키지 이미지" />
                    <div :class="$style.imageOverlayActions">
                      <label :class="$style.imageChangeBtn" :tabindex="saving ? -1 : 0">
                        변경
                        <input type="file" accept="image/*" @change="(e) => onRewardTierImageChange(e, tIdx, imgIdx)"
                          :disabled="saving" style="display:none" />
                      </label>
                      <button type="button" :class="$style.imageRemoveBtn"
                        @click="removeRewardTierImage(tIdx, imgIdx)" :disabled="saving">
                        삭제
                      </button>
                    </div>
                  </div>
                  <label v-if="tier.images.length < 10"
                    :class="[$style.imageUploadBtn, saving ? $style.imageUploadBtnDisabled : '']">
                    <span>+ 이미지 추가</span>
                    <input type="file" accept="image/*" @change="(e) => onRewardTierImageChange(e, tIdx, -1)"
                      :disabled="saving" style="display:none" />
                  </label>
                </div>
              </div>

              <div :class="$style.input">
                <h4>최대 선택 수량</h4>
                <input :class="$style.textInput" v-model.number="tier.maxSelection" type="number" min="1"
                  placeholder="비워두면 무제한" :disabled="saving" />
              </div>

              <div :class="$style.input">
                <h4 :class="$style.required">구성품</h4>
                <div v-for="(item, iIdx) in tier.items" :key="iIdx"
                  style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
                  <input :class="$style.textInput" v-model="item.name" placeholder="구성품명" :disabled="saving"
                    style="flex:1;" />
                  <input :class="$style.textInput" v-model.number="item.quantity" type="number" min="1" placeholder="수량"
                    :disabled="saving" style="width:80px;" />
                  <button type="button" :class="$style.deleteButton" @click="removeRewardItem(tIdx, iIdx)"
                    :disabled="saving || tier.items.length <= 1" style="padding:6px 10px;font-size:12px;">✕</button>
                </div>
                <button type="button" @click="addRewardItem(tIdx)" :class="$style.addOptionButton" :disabled="saving">
                  + 구성품 추가
                </button>
              </div>
            </div>

            <button type="button" @click="addRewardTier" :class="$style.addFieldButton" :disabled="saving">
              + 패키지 추가
            </button>
          </section>

          <!-- 추가구매항목 섹션 -->
          <section :class="$style.section">
            <div :class="$style.sectionHeader">
              <h2 :class="$style.sectionTitle">추가구매항목</h2>
              <p :class="$style.sectionDescription">티켓 외에 함께 판매할 굿즈, 음료 등을 설정하세요</p>
            </div>

            <!-- 각 추가구매항목 카드 -->
            <div v-for="(item, index) in purchaseItems" :key="item.id" :class="$style.questionCard">
              <div :class="$style.questionHeader">
                <span :class="$style.questionNumber">항목 {{ index + 1 }}</span>
                <div :class="$style.headerActions">
                  <div :class="$style.reorderControls">
                    <button type="button" :class="$style.reorderButton" @click="movePurchaseItem(index, -1)"
                      :disabled="saving || index === 0" aria-label="위로">
                      ↑
                    </button>
                    <button type="button" :class="$style.reorderButton" @click="movePurchaseItem(index, 1)"
                      :disabled="saving || index === purchaseItems.length - 1" aria-label="아래로">
                      ↓
                    </button>
                  </div>
                  <button type="button" @click="removePurchaseItem(index)" :class="$style.deleteButton"
                    :disabled="saving">
                    삭제
                  </button>
                </div>
              </div>

              <div :class="$style.input">
                <h4 :class="$style.required">항목명</h4>
                <input :class="$style.textInput" v-model="item.name" placeholder="예: MD굿즈, 음료, 주차권"
                  :disabled="saving" />
              </div>

              <div :class="$style.input">
                <h4>설명 <span style="font-size: 12px; color: #999;">(선택)</span></h4>
                <input :class="$style.textInput" v-model="item.description" placeholder="예: 오리지널 MD 티셔츠"
                  :disabled="saving" />
              </div>

              <!-- 이미지 업로드 -->
              <div :class="$style.input">
                <h4>이미지 <span style="font-size: 12px; color: #999;">(선택, 최대 10개)</span></h4>
                <div :class="$style.imageGridArea">
                  <div v-for="(img, imgIdx) in item.images" :key="imgIdx" :class="$style.imagePreviewWrapper">
                    <img :src="img.previewUrl || img.url" :class="$style.imagePreview" alt="굿즈 이미지" />
                    <div :class="$style.imageOverlayActions">
                      <label :class="$style.imageChangeBtn" :tabindex="saving ? -1 : 0">
                        변경
                        <input type="file" accept="image/*" @change="(e) => onPurchaseItemImageChange(e, index, imgIdx)"
                          :disabled="saving" style="display:none" />
                      </label>
                      <button type="button" :class="$style.imageRemoveBtn"
                        @click="removePurchaseItemImage(index, imgIdx)" :disabled="saving">
                        삭제
                      </button>
                    </div>
                  </div>
                  <label v-if="item.images.length < 10"
                    :class="[$style.imageUploadBtn, saving ? $style.imageUploadBtnDisabled : '']">
                    <span>+ 이미지 추가</span>
                    <input type="file" accept="image/*" @change="(e) => onPurchaseItemImageChange(e, index, -1)"
                      :disabled="saving" style="display:none" />
                  </label>
                </div>
              </div>

              <div :class="$style.input">
                <h4 :class="$style.required">가격</h4>
                <input type="number" :class="$style.textInput" v-model.number="item.price" placeholder="15000"
                  :disabled="saving" min="0" />
              </div>

              <div :class="$style.input">
                <h4>최대 구매 가능 수량 <span style="font-size: 12px; color: #999;">(미입력 시 제한 없음)</span></h4>
                <input type="number" :class="$style.textInput" v-model.number="item.maxQuantity" placeholder="예: 100"
                  :disabled="saving" min="0" />
              </div>

              <div :class="$style.input">
                <label :class="$style.checkboxLabel">
                  <input type="checkbox" v-model="item.required" :disabled="saving" :class="$style.checkbox" />
                  필수 구매 항목으로 설정
                </label>
              </div>

              <!-- 옵션 설정 버튼 또는 옵션 필드 -->
              <div v-if="!item.hasOption">
                <button type="button" @click="enablePurchaseItemOption(index)" :class="$style.enableOptionButton"
                  :disabled="saving">
                  + 옵션 설정 (사이즈, 색상 등)
                </button>
              </div>

              <template v-else>
                <div :class="$style.optionDivider">
                  <span>추가 옵션</span>
                  <button type="button" @click="disablePurchaseItemOption(index)" :class="$style.removeOptionButton2"
                    :disabled="saving">
                    옵션 제거
                  </button>
                </div>

                <div :class="$style.input">
                  <h4>옵션 유형</h4>
                  <div :class="$style.radio">
                    <label>
                      <input type="radio" value="text" v-model="item.optionType" :disabled="saving" />
                      단답형
                    </label>
                    <label>
                      <input type="radio" value="textarea" v-model="item.optionType" :disabled="saving" />
                      장문형
                    </label>
                    <label>
                      <input type="radio" value="radio" v-model="item.optionType" :disabled="saving" />
                      객관식
                    </label>
                    <label>
                      <input type="radio" value="checkbox" v-model="item.optionType" :disabled="saving" />
                      체크박스
                    </label>
                  </div>
                </div>

                <div :class="$style.input">
                  <h4>옵션명 <span style="font-size: 12px; color: #999;">(예: 사이즈, 색상)</span></h4>
                  <input :class="$style.textInput" v-model="item.optionTitle" placeholder="예: 티셔츠 사이즈"
                    :disabled="saving" />
                </div>

                <div :class="$style.input">
                  <h4>옵션 설명 <span style="font-size: 12px; color: #999;">(선택)</span></h4>
                  <input :class="$style.textInput" v-model="item.optionDescription" placeholder="예: 원하시는 사이즈를 선택해주세요"
                    :disabled="saving" />
                </div>

                <!-- 객관식/체크박스 선택지 -->
                <div v-if="item.optionType === 'radio' || item.optionType === 'checkbox'" :class="$style.input">
                  <h4>선택지</h4>
                  <div v-for="(option, optionIndex) in item.options" :key="optionIndex" :class="$style.optionRow">
                    <input :class="$style.textInput" v-model="item.options[optionIndex]"
                      :placeholder="'선택지 ' + (optionIndex + 1)" :disabled="saving" />
                    <button type="button" @click="removePurchaseItemOption(index, optionIndex)"
                      :class="$style.removeOptionButton" :disabled="saving || item.options.length <= 1">
                      ×
                    </button>
                  </div>
                  <button type="button" @click="addPurchaseItemOption(index)" :class="$style.addOptionButton"
                    :disabled="saving">
                    + 선택지 추가
                  </button>
                </div>

                <!-- 체크박스 선택 개수 제한 -->
                <div v-if="item.optionType === 'checkbox'" :class="$style.input">
                  <div :class="$style.limitInputGroup">
                    <div :class="$style.limitInputItem">
                      <h4>최소 선택 개수 <span style="font-size: 12px; color: #999;">(선택)</span></h4>
                      <input type="number" :class="$style.textInput" v-model.number="item.minSelection" placeholder="0"
                        :disabled="saving" min="0" :max="item.options ? item.options.length : 999" />
                    </div>
                    <div :class="$style.limitInputItem">
                      <h4>최대 선택 개수 <span style="font-size: 12px; color: #999;">(선택)</span></h4>
                      <input type="number" :class="$style.textInput" v-model.number="item.maxSelection"
                        placeholder="제한 없음" :disabled="saving" min="0"
                        :max="item.options ? item.options.length : 999" />
                    </div>
                  </div>
                  <p :class="$style.limitHint">
                    💡 미입력 시: 최소 0개, 최대 제한 없음
                  </p>
                </div>
              </template>
            </div>

            <!-- 추가구매항목 추가 버튼 -->
            <button type="button" @click="addPurchaseItem" :class="$style.addFieldButton" :disabled="saving">
              + 추가구매항목 추가
            </button>
          </section>

          <!-- 커스텀 질문 섹션 -->
          <section :class="$style.section">
            <div :class="$style.sectionHeader">
              <h2 :class="$style.sectionTitle">커스텀 질문</h2>
              <p :class="$style.sectionDescription">예매자에게 받을 추가 정보를 설정하세요</p>
              <div :class="$style.autoIncludeNotice">
                <span :class="$style.autoIncludeIcon">✓</span>
                <div :class="$style.autoIncludeContent">
                  <span :class="$style.autoIncludeLabel">자동 포함</span>
                  <p :class="$style.autoIncludeText">입금자명, 이메일은 링꾹라이브 계정 정보로 자동 수집됩니다</p>
                </div>
              </div>
            </div>

            <!-- 각 질문 카드 -->
            <div v-for="(field, index) in customFields" :key="field.id" :class="$style.questionCard">
              <div :class="$style.questionHeader">
                <span :class="$style.questionNumber">질문 {{ index + 1 }}</span>
                <div :class="$style.headerActions">
                  <div :class="$style.reorderControls">
                    <button type="button" :class="$style.reorderButton" @click="moveField(index, -1)"
                      :disabled="saving || index === 0" aria-label="위로">
                      ↑
                    </button>
                    <button type="button" :class="$style.reorderButton" @click="moveField(index, 1)"
                      :disabled="saving || index === customFields.length - 1" aria-label="아래로">
                      ↓
                    </button>
                  </div>
                  <button type="button" @click="removeField(index)" :class="$style.deleteButton" :disabled="saving">
                    삭제
                  </button>
                </div>
              </div>

              <div :class="$style.input">
                <h4 :class="$style.required">질문 제목</h4>
                <input :class="$style.textInput" v-model="field.title"
                  placeholder="예: 어떤 밴드를 보러 오셨나요?, 동반인 수, 가장 기대되는 아티스트" :disabled="saving" />
              </div>

              <div :class="$style.input">
                <h4>설명 <span style="font-size: 12px; color: #999;">(선택)</span></h4>
                <input :class="$style.textInput" v-model="field.description" placeholder="예: 라인업 확인을 위해 알려주세요"
                  :disabled="saving" />
              </div>

              <div :class="$style.input">
                <h4>답변 유형</h4>
                <div :class="$style.radio">
                  <label>
                    <input type="radio" value="text" v-model="field.type" :disabled="saving" />
                    단답형
                  </label>
                  <label>
                    <input type="radio" value="textarea" v-model="field.type" :disabled="saving" />
                    장문형
                  </label>
                  <label>
                    <input type="radio" value="radio" v-model="field.type" :disabled="saving" />
                    객관식
                  </label>
                  <label>
                    <input type="radio" value="checkbox" v-model="field.type" :disabled="saving" />
                    체크박스
                  </label>
                </div>
              </div>

              <!-- 객관식/체크박스 옵션 설정 -->
              <div v-if="field.type === 'radio' || field.type === 'checkbox'" :class="$style.input">
                <h4>선택지</h4>
                <div v-for="(option, optionIndex) in field.options" :key="optionIndex" :class="$style.optionRow">
                  <input :class="$style.textInput" v-model="field.options[optionIndex]"
                    :placeholder="'옵션 ' + (optionIndex + 1)" :disabled="saving" />
                  <button type="button" @click="removeOption(index, optionIndex)" :class="$style.removeOptionButton"
                    :disabled="saving || field.options.length <= 1">
                    ×
                  </button>
                </div>
                <button type="button" @click="addOption(index)" :class="$style.addOptionButton" :disabled="saving">
                  + 선택지 추가
                </button>
              </div>

              <!-- 이미지 업로드 -->
              <div :class="$style.input">
                <h4>이미지 <span style="font-size: 12px; color: #999;">(선택, 최대 10개)</span></h4>
                <div :class="$style.imageGridArea">
                  <div v-for="(img, imgIdx) in field.images" :key="imgIdx" :class="$style.imagePreviewWrapper">
                    <img :src="img.previewUrl || img.url" :class="$style.imagePreview" alt="질문 이미지" />
                    <div :class="$style.imageOverlayActions">
                      <label :class="$style.imageChangeBtn" :tabindex="saving ? -1 : 0">
                        변경
                        <input type="file" accept="image/*" @change="(e) => onCustomFieldImageChange(e, index, imgIdx)"
                          :disabled="saving" style="display:none" />
                      </label>
                      <button type="button" :class="$style.imageRemoveBtn"
                        @click="removeCustomFieldImage(index, imgIdx)" :disabled="saving">
                        삭제
                      </button>
                    </div>
                  </div>
                  <label v-if="field.images.length < 10"
                    :class="[$style.imageUploadBtn, saving ? $style.imageUploadBtnDisabled : '']">
                    <span>+ 이미지 추가</span>
                    <input type="file" accept="image/*" @change="(e) => onCustomFieldImageChange(e, index, -1)"
                      :disabled="saving" style="display:none" />
                  </label>
                </div>
              </div>

              <div :class="$style.input">
                <label :class="$style.checkboxLabel">
                  <input type="checkbox" v-model="field.required" :disabled="saving" :class="$style.checkbox" />
                  필수 질문으로 설정
                </label>
              </div>
            </div>

            <!-- 질문 추가 버튼 -->
            <button type="button" @click="addField" :class="$style.addFieldButton" :disabled="saving">
              + 질문 추가
            </button>
          </section>

          <!-- 저장 버튼 -->
          <div :class="$style.formActions">
            <button type="button" :class="$style.closeBottomButton" @click="$router.back()" :disabled="saving">
              닫기
            </button>
            <button type="button" :class="$style.previewButton" @click="previewForm"
              :disabled="!commissionId || saving">
              미리보기
            </button>
            <button type="submit" :class="$style.saveButton" :disabled="saving">
              {{ saving ? "저장 중..." : "저장하기" }}
            </button>
          </div>
        </form>

      </div>
    </div>
  </div>
</template>

<script>
import { firestore, storage } from "../../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { savePurchaseForm } from "@/utils/write/purchase";
import { useUserStore } from "@/store/user";

export default {
  name: "CommissionFormAdmin",
  data() {
    return {
      userStore: useUserStore(),
      loading: true,
      error: null,
      saving: false,
      commissionId: "",
      commission: null,
      isNewForm: false,
      noticeFields: [],
      noticeIdCounter: 0,
      cancelPolicy: { enabled: false, content: "" },
      rewardTiers: [],
      rewardTierIdCounter: 0,
      purchaseItems: [],
      purchaseItemIdCounter: 0,
      customFields: [],
      fieldIdCounter: 0,
    };
  },
  async mounted() {
    if (!this.userStore.isLogin) {
      this.$alert("로그인이 필요합니다.");
      this.$router.push("/");
      return;
    }

    this.commissionId = this.$route.params.id || "";

    if (!this.commissionId) {
      this.error = "잘못된 접근입니다. 커미션 상세 페이지에서 접근해주세요.";
      this.loading = false;
      return;
    }

    try {
      await this.loadFormInfo();
      // live 데이터 로드 후 권한 확인 (organizer 정보 필요)
      if (!this.checkAuth()) return;
    } catch (err) {
      console.error("초기화 오류:", err);
      this.error = "페이지를 불러오는 중 오류가 발생했습니다.";
    } finally {
      this.loading = false;
    }
  },
  methods: {

    checkAuth() {
      const isAdmin = this.userStore.auth === "admin";
      const ownerEmail = this.commission?.createdBy;
      const isOwner = ownerEmail && ownerEmail === this.userStore.email;

      if (!isAdmin && !isOwner) {
        this.$alert("접근 권한이 없습니다.");
        this.$router.push("/");
        return false;
      }
      return true;
    },
    async loadFormInfo() {
      if (!this.commissionId) {
        this.commission = null;
        this.isNewForm = false;
        return;
      }

      try {
        const commissionDoc = await getDoc(
          doc(firestore, "commissions", this.commissionId)
        );

        if (!commissionDoc.exists()) {
          this.$alert("해당 커미션 정보를 찾을 수 없습니다.");
          this.commission = null;
          this.isNewForm = false;
          return;
        }

        this.commission = {
          id: commissionDoc.id,
          ...commissionDoc.data(),
        };

        const formDoc = await getDoc(
          doc(firestore, "purchaseForm", this.commissionId)
        );
        if (formDoc.exists()) {
          const data = formDoc.data();

          // 취소/환불 규정 로드
          if (data.cancelPolicy) {
            this.cancelPolicy = {
              enabled: data.cancelPolicy.enabled || false,
              content: data.cancelPolicy.content || "",
            };
          } else {
            this.cancelPolicy = { enabled: false, content: "" };
          }

          // 안내사항 로드
          if (data.noticeFields && Array.isArray(data.noticeFields)) {
            this.noticeFields = data.noticeFields.map(notice => ({
              ...notice,
              id: this.noticeIdCounter++
            }));
          } else {
            this.noticeFields = [];
          }

          // 티켓 패키지 로드
          if (data.rewardTiers && Array.isArray(data.rewardTiers)) {
            this.rewardTiers = data.rewardTiers.map(tier => ({
              ...tier,
              id: this.rewardTierIdCounter++,
              items: Array.isArray(tier.items) ? tier.items : [],
              images: Array.isArray(tier.imageUrls) && tier.imageUrls.length > 0
                ? tier.imageUrls.map(url => ({ url, file: null, previewUrl: null }))
                : [],
            }));
          } else {
            this.rewardTiers = [];
          }

          // 추가구매항목 로드
          if (data.purchaseItems && Array.isArray(data.purchaseItems)) {
            this.purchaseItems = data.purchaseItems.map(item => ({
              ...item,
              id: this.purchaseItemIdCounter++,
              images: Array.isArray(item.imageUrls) && item.imageUrls.length > 0
                ? item.imageUrls.map(url => ({ url, file: null, previewUrl: null }))
                : (item.imageUrl ? [{ url: item.imageUrl, file: null, previewUrl: null }] : []),
              hasOption: item.hasOption || false,
              optionType: item.optionType || "text",
              optionTitle: item.optionTitle || "",
              optionDescription: item.optionDescription || "",
              options: item.options || [],
              minSelection: item.minSelection || null,
              maxSelection: item.maxSelection || null,
              optionRequired: item.hasOption ? true : false
            }));
          } else {
            this.purchaseItems = [];
          }

          // 커스텀 질문 로드
          if (data.customFields && Array.isArray(data.customFields)) {
            this.customFields = data.customFields.map(field => ({
              ...field,
              id: this.fieldIdCounter++,
              images: Array.isArray(field.imageUrls) && field.imageUrls.length > 0
                ? field.imageUrls.map(url => ({ url, file: null, previewUrl: null }))
                : (field.imageUrl ? [{ url: field.imageUrl, file: null, previewUrl: null }] : []),
            }));
          } else {
            this.customFields = [];
          }

          this.isNewForm = false;
        } else {
          this.noticeFields = [];
          this.cancelPolicy = { enabled: false, content: "" };
          this.rewardTiers = [];
          this.purchaseItems = [];
          this.customFields = [];
          this.isNewForm = true;
        }
      } catch (err) {
        console.error("폼 정보 로드 오류:", err);
        this.$alert(`폼 정보를 불러오는 중 오류가 발생했습니다.\n오류: ${err.message || err}`);
      }
    },

    async saveFormInfo() {
      if (!this.commissionId) return;

      // 안내사항 유효성 검사
      for (let i = 0; i < this.noticeFields.length; i++) {
        const notice = this.noticeFields[i];
        if (!notice.title || notice.title.trim() === "") {
          this.$alert(`안내 ${i + 1}의 제목을 입력해주세요.`);
          return;
        }
        if (!notice.content || notice.content.trim() === "") {
          this.$alert(`안내 ${i + 1}의 내용을 입력해주세요.`);
          return;
        }
      }

      // 티켓 패키지 유효성 검사
      for (let i = 0; i < this.rewardTiers.length; i++) {
        const tier = this.rewardTiers[i];
        if (!tier.name || tier.name.trim() === "") {
          this.$alert(`패키지 ${i + 1}의 이름을 입력해주세요.`);
          return;
        }
        if (tier.price === null || tier.price === undefined || tier.price < 0) {
          this.$alert(`패키지 ${i + 1}의 가격을 입력해주세요.`);
          return;
        }
        if (!tier.items || tier.items.length === 0) {
          this.$alert(`패키지 ${i + 1}에 구성품을 1개 이상 추가해주세요.`);
          return;
        }
        for (let j = 0; j < tier.items.length; j++) {
          if (!tier.items[j].name || tier.items[j].name.trim() === "") {
            this.$alert(`패키지 ${i + 1}의 구성품 ${j + 1} 이름을 입력해주세요.`);
            return;
          }
        }
      }

      // 추가구매항목 유효성 검사
      for (let i = 0; i < this.purchaseItems.length; i++) {
        const item = this.purchaseItems[i];
        if (!item.name || item.name.trim() === "") {
          this.$alert(`추가구매항목 ${i + 1}의 항목명을 입력해주세요.`);
          return;
        }
        if (item.price === null || item.price === undefined || item.price < 0) {
          this.$alert(`추가구매항목 ${i + 1}의 가격을 입력해주세요.`);
          return;
        }
        // 옵션 유효성 검사
        if (item.hasOption) {
          if (!item.optionTitle || item.optionTitle.trim() === "") {
            this.$alert(`추가구매항목 ${i + 1}의 옵션명을 입력해주세요.`);
            return;
          }
          if ((item.optionType === 'radio' || item.optionType === 'checkbox') &&
            (!item.options || item.options.length === 0 || !item.options.some(o => o.trim()))) {
            this.$alert(`추가구매항목 ${i + 1}의 선택지를 추가해주세요.`);
            return;
          }
          // 체크박스 선택 개수 검증
          if (item.optionType === 'checkbox') {
            const optionsCount = item.options ? item.options.length : 0;
            if (item.minSelection !== null && item.minSelection !== undefined) {
              if (item.minSelection < 0) {
                this.$alert(`추가구매항목 ${i + 1}의 최소 선택 개수는 0 이상이어야 합니다.`);
                return;
              }
              if (item.minSelection > optionsCount) {
                this.$alert(`추가구매항목 ${i + 1}의 최소 선택 개수는 선택지 개수(${optionsCount})보다 클 수 없습니다.`);
                return;
              }
            }
            if (item.maxSelection !== null && item.maxSelection !== undefined) {
              if (item.maxSelection < 0) {
                this.$alert(`추가구매항목 ${i + 1}의 최대 선택 개수는 0 이상이어야 합니다.`);
                return;
              }
              if (item.maxSelection > optionsCount) {
                this.$alert(`추가구매항목 ${i + 1}의 최대 선택 개수는 선택지 개수(${optionsCount})보다 클 수 없습니다.`);
                return;
              }
            }
            if (item.minSelection !== null && item.maxSelection !== null &&
              item.minSelection > item.maxSelection) {
              this.$alert(`추가구매항목 ${i + 1}의 최소 선택 개수는 최대 선택 개수보다 클 수 없습니다.`);
              return;
            }
          }
        }
      }

      // 커스텀 질문 유효성 검사
      for (let i = 0; i < this.customFields.length; i++) {
        const field = this.customFields[i];
        if (!field.title || field.title.trim() === "") {
          this.$alert(`질문 ${i + 1}의 제목을 입력해주세요.`);
          return;
        }
        if ((field.type === 'radio' || field.type === 'checkbox') && field.options.length === 0) {
          this.$alert(`질문 ${i + 1}의 선택지를 추가해주세요.`);
          return;
        }
      }

      try {
        this.saving = true;

        // 티켓 패키지 이미지 업로드
        for (let i = 0; i < this.rewardTiers.length; i++) {
          const tier = this.rewardTiers[i];
          for (let j = 0; j < tier.images.length; j++) {
            const img = tier.images[j];
            if (img.file) {
              const ext = img.file.name.split(".").pop() || "jpg";
              const path = `purchaseFormImages/${this.commissionId}/reward_${i}_${j}_${Date.now()}.${ext}`;
              const fileRef = storageRef(storage, path);
              await uploadBytes(fileRef, img.file);
              img.url = await getDownloadURL(fileRef);
              img.file = null;
              if (img.previewUrl) {
                URL.revokeObjectURL(img.previewUrl);
                img.previewUrl = null;
              }
            }
          }
        }

        // 추가구매항목 이미지 업로드
        for (let i = 0; i < this.purchaseItems.length; i++) {
          const item = this.purchaseItems[i];
          for (let j = 0; j < item.images.length; j++) {
            const img = item.images[j];
            if (img.file) {
              const ext = img.file.name.split(".").pop() || "jpg";
              const path = `purchaseFormImages/${this.commissionId}/item_${i}_${j}_${Date.now()}.${ext}`;
              const fileRef = storageRef(storage, path);
              await uploadBytes(fileRef, img.file);
              img.url = await getDownloadURL(fileRef);
              img.file = null;
              if (img.previewUrl) {
                URL.revokeObjectURL(img.previewUrl);
                img.previewUrl = null;
              }
            }
          }
        }

        // 커스텀 질문 이미지 업로드
        for (let i = 0; i < this.customFields.length; i++) {
          const field = this.customFields[i];
          for (let j = 0; j < field.images.length; j++) {
            const img = field.images[j];
            if (img.file) {
              const ext = img.file.name.split(".").pop() || "jpg";
              const path = `purchaseFormImages/${this.commissionId}/field_${i}_${j}_${Date.now()}.${ext}`;
              const fileRef = storageRef(storage, path);
              await uploadBytes(fileRef, img.file);
              img.url = await getDownloadURL(fileRef);
              img.file = null;
              if (img.previewUrl) {
                URL.revokeObjectURL(img.previewUrl);
                img.previewUrl = null;
              }
            }
          }
        }

        // 취소/환불 규정 유효성 검사
        if (this.cancelPolicy.enabled && !this.cancelPolicy.content.trim()) {
          this.$alert("취소/환불 규정 내용을 입력해주세요.");
          this.saving = false;
          return;
        }

        const formDataToSave = {
          liveId: this.commissionId,
          liveTitle: this.commission.name,
          cancelPolicy: {
            enabled: this.cancelPolicy.enabled,
            content: this.cancelPolicy.enabled ? this.cancelPolicy.content.trim() : "",
          },
          noticeFields: this.noticeFields.map(notice => ({
            title: notice.title,
            content: notice.content,
          })),
          rewardTiers: this.rewardTiers.map(tier => ({
            name: tier.name,
            price: tier.price,
            description: tier.description || "",
            items: (tier.items || []).map(i => ({
              name: i.name,
              quantity: i.quantity || 1,
            })),
            maxSelection: tier.maxSelection || null,
            imageUrls: tier.images.map(img => img.url).filter(Boolean),
          })),
          purchaseItems: this.purchaseItems.map(item => ({
            name: item.name,
            description: item.description || "",
            price: item.price,
            maxQuantity: item.maxQuantity || null,
            imageUrls: item.images.map(img => img.url).filter(Boolean),
            hasOption: item.hasOption || false,
            optionType: item.optionType || "text",
            optionTitle: item.optionTitle || "",
            optionDescription: item.optionDescription || "",
            options: item.options || [],
            minSelection: item.minSelection || null,
            maxSelection: item.maxSelection || null,
            optionRequired: item.hasOption ? true : false,
            required: item.required || false
          })),
          customFields: this.customFields.map(field => ({
            title: field.title,
            description: field.description || "",
            type: field.type,
            options: field.options || [],
            required: field.required || false,
            imageUrls: field.images.map(img => img.url).filter(Boolean),
          }))
        };

        await savePurchaseForm(this.commissionId, formDataToSave);

        this.isNewForm = false;

        this.$alert("저장되었습니다!");
      } catch (err) {
        console.error("저장 오류:", err);
        this.$alert(`저장 중 오류가 발생했습니다.\n오류: ${err.message || err}`);
      } finally {
        this.saving = false;
      }
    },
    previewForm() {
      if (!this.commissionId) return;

      if (this.isNewForm) {
        this.$alert("저장 후 미리보기를 사용할 수 있습니다.");
        return;
      }

      window.open(`/commission-purchase/${this.commissionId}?preview=true`, "_blank");
    },
    addNotice() {
      this.noticeFields.push({
        id: this.noticeIdCounter++,
        title: "",
        content: ""
      });
    },
    moveArrayItem(list, fromIndex, toIndex) {
      if (!Array.isArray(list)) return;
      if (fromIndex === toIndex) return;
      if (fromIndex < 0 || fromIndex >= list.length) return;
      if (toIndex < 0 || toIndex >= list.length) return;
      const [item] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, item);
    },
    moveNotice(index, direction) {
      this.moveArrayItem(this.noticeFields, index, index + direction);
    },
    async removeNotice(index) {
      const result = await this.$confirm("이 안내사항을 삭제하시겠습니까?");
      if (result) {
        this.noticeFields.splice(index, 1);
      }
    },
    addRewardTier() {
      this.rewardTiers.push({
        id: this.rewardTierIdCounter++,
        name: "",
        price: 0,
        description: "",
        items: [{ name: "", quantity: 1 }],
        maxSelection: null,
        images: [],
      });
    },
    removeRewardTier(index) {
      this.rewardTiers.splice(index, 1);
    },
    moveRewardTier(index, direction) {
      this.moveArrayItem(this.rewardTiers, index, index + direction);
    },
    addRewardItem(tierIndex) {
      this.rewardTiers[tierIndex].items.push({ name: "", quantity: 1 });
    },
    removeRewardItem(tierIndex, itemIndex) {
      this.rewardTiers[tierIndex].items.splice(itemIndex, 1);
    },
    onRewardTierImageChange(event, tierIndex, imgIndex) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        this.$alert("이미지 파일만 업로드할 수 있습니다.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$alert("이미지 크기는 5MB 이하여야 합니다.");
        return;
      }
      const tier = this.rewardTiers[tierIndex];
      if (imgIndex === -1) {
        tier.images.push({ url: null, file, previewUrl: URL.createObjectURL(file) });
      } else {
        const img = tier.images[imgIndex];
        if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
        img.file = file;
        img.previewUrl = URL.createObjectURL(file);
        img.url = null;
      }
      event.target.value = "";
    },
    removeRewardTierImage(tierIndex, imgIndex) {
      const tier = this.rewardTiers[tierIndex];
      const img = tier.images[imgIndex];
      if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
      tier.images.splice(imgIndex, 1);
    },
    addPurchaseItem() {
      this.purchaseItems.push({
        id: this.purchaseItemIdCounter++,
        name: "",
        description: "",
        price: 0,
        maxQuantity: null,
        images: [],
        hasOption: false,
        optionType: "text",
        optionTitle: "",
        optionDescription: "",
        options: [],
        minSelection: null,
        maxSelection: null,
        optionRequired: false,
        required: false
      });
    },
    onPurchaseItemImageChange(event, itemIndex, imgIndex) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        this.$alert("이미지 파일만 업로드할 수 있습니다.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$alert("이미지 크기는 5MB 이하여야 합니다.");
        return;
      }

      const item = this.purchaseItems[itemIndex];
      if (imgIndex === -1) {
        item.images.push({ url: null, file, previewUrl: URL.createObjectURL(file) });
      } else {
        const img = item.images[imgIndex];
        if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
        img.file = file;
        img.previewUrl = URL.createObjectURL(file);
        img.url = null;
      }
      event.target.value = "";
    },

    removePurchaseItemImage(itemIndex, imgIndex) {
      const item = this.purchaseItems[itemIndex];
      const img = item.images[imgIndex];
      if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
      item.images.splice(imgIndex, 1);
    },

    enablePurchaseItemOption(itemIndex) {
      this.purchaseItems[itemIndex].hasOption = true;
      this.purchaseItems[itemIndex].optionType = "text";
      this.purchaseItems[itemIndex].options = [];
      this.purchaseItems[itemIndex].optionRequired = true;
    },
    disablePurchaseItemOption(itemIndex) {
      this.purchaseItems[itemIndex].hasOption = false;
      this.purchaseItems[itemIndex].optionType = "text";
      this.purchaseItems[itemIndex].optionTitle = "";
      this.purchaseItems[itemIndex].optionDescription = "";
      this.purchaseItems[itemIndex].options = [];
      this.purchaseItems[itemIndex].minSelection = null;
      this.purchaseItems[itemIndex].maxSelection = null;
      this.purchaseItems[itemIndex].optionRequired = false;
    },
    addPurchaseItemOption(itemIndex) {
      if (!this.purchaseItems[itemIndex].options) {
        this.purchaseItems[itemIndex].options = [];
      }
      this.purchaseItems[itemIndex].options.push("");
    },
    removePurchaseItemOption(itemIndex, optionIndex) {
      this.purchaseItems[itemIndex].options.splice(optionIndex, 1);
    },
    movePurchaseItem(index, direction) {
      this.moveArrayItem(this.purchaseItems, index, index + direction);
    },
    async removePurchaseItem(index) {
      const result = await this.$confirm("이 추가구매항목을 삭제하시겠습니까?");
      if (result) {
        this.purchaseItems.splice(index, 1);
      }
    },
    addField() {
      this.customFields.push({
        id: this.fieldIdCounter++,
        title: "",
        description: "",
        type: "text",
        options: [],
        required: false,
        images: [],
      });
    },
    moveField(index, direction) {
      this.moveArrayItem(this.customFields, index, index + direction);
    },
    async removeField(index) {
      const result = await this.$confirm("이 질문을 삭제하시겠습니까?");
      if (result) {
        this.customFields.splice(index, 1);
      }
    },
    addOption(fieldIndex) {
      if (!this.customFields[fieldIndex].options) {
        this.customFields[fieldIndex].options = [];
      }
      this.customFields[fieldIndex].options.push("");
    },
    removeOption(fieldIndex, optionIndex) {
      this.customFields[fieldIndex].options.splice(optionIndex, 1);
    },

    onCustomFieldImageChange(event, fieldIndex, imgIndex) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        this.$alert("이미지 파일만 업로드할 수 있습니다.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$alert("이미지 크기는 5MB 이하여야 합니다.");
        return;
      }

      const field = this.customFields[fieldIndex];
      if (imgIndex === -1) {
        field.images.push({ url: null, file, previewUrl: URL.createObjectURL(file) });
      } else {
        const img = field.images[imgIndex];
        if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
        img.file = file;
        img.previewUrl = URL.createObjectURL(file);
        img.url = null;
      }
      event.target.value = "";
    },

    removeCustomFieldImage(fieldIndex, imgIndex) {
      const field = this.customFields[fieldIndex];
      const img = field.images[imgIndex];
      if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
      field.images.splice(imgIndex, 1);
    },
  },
};
</script>

<style module>
/* PaperModalContainer 스타일 */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  z-index: 900;
}

.paper {
  position: fixed;
  width: 100%;
  max-width: 1024px;
  height: 100%;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  background-color: #fff;
  padding: 18px;
  border-radius: 0;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
  text-align: center;
  max-height: 100vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.closeButton {
  position: fixed;
  top: 12px;
  right: 12px;
  background: #fff;
  border: none;
  font-size: 24px;
  font-weight: bold;
  color: #999;
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 1001;
}

.closeButton:hover {
  background-color: #f5f5f5;
  color: #666;
  transform: scale(1.1);
}

.closeButton:active {
  transform: scale(0.95);
  background-color: #e0e0e0;
}

.logo {
  width: 65%;
  max-width: 300px;
  margin-top: 20px;
}

.title {
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  margin: 15px 0 5px 0;
  font-size: 18px;
}

.inputs {
  width: 96%;
  margin: auto;
  text-align: left;
}

.loading,
.error {
  text-align: center;
  padding: 48px;
  font-size: 16px;
  color: #666;
}

.error {
  color: #ff4444;
}

.formContainer {
  max-width: 900px;
  margin: 0 auto;
  text-align: left;
}

.section {
  background: transparent;
  border-radius: 0;
  padding: 32px 0;
  margin-bottom: 32px;
  border-bottom: 1px solid #e9ecef;
}

.section:last-of-type {
  border-bottom: none;
}

.sectionHeader {
  margin-bottom: 24px;
}

.sectionTitle {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
  font-family: "NanumSquareRoundEB", sans-serif;
}

.sectionDescription {
  font-size: 14px;
  color: #666;
  margin: 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

.autoIncludeNotice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 12px 0 0 0;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #66bdff;
  border-radius: 8px;
  font-family: "NanumSquareRoundR", sans-serif;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.autoIncludeIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  background: #66bdff;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  font-family: "NanumSquareRoundB", sans-serif;
}

.autoIncludeContent {
  flex: 1;
  min-width: 0;
}

.autoIncludeLabel {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: #66bdff;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 4px;
  font-family: "NanumSquareRoundB", sans-serif;
}

.autoIncludeText {
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
  margin: 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

.input {
  margin-bottom: 20px;
}

.input h4 {
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #444;
  font-family: "NanumSquareRoundB", sans-serif;
}

.required::after {
  content: "*";
  color: #ff4444;
  font-size: 12px;
  font-weight: bold;
  margin-left: 4px;
  vertical-align: top;
  line-height: 1;
}

.textInput {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.textInput:focus {
  outline: none;
  border-color: #66bdff;
}

.textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  resize: vertical;
  transition: border-color 0.2s;
  box-sizing: border-box;
  line-height: 1.6;
}

.textarea:focus {
  outline: none;
  border-color: #66bdff;
}

.picker {
  width: 100%;
  height: 44px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.radio {
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.radio input[type="radio"] {
  display: none;
}

.radio label {
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  padding: 10px 20px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #666;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;
  font-weight: 400;
  position: relative;
  display: block;
  flex: 1;
  min-width: 80px;
}

.radio label:hover {
  border-color: #66bdff;
  color: #66bdff;
}

.radio label:has(input[type="radio"]:checked) {
  border-color: #66bdff;
  background: #66bdff;
  color: #fff;
  font-weight: 500;
}

.checkboxLabel {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
}

.checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #66bdff;
}

/* 커스텀 질문 카드 */
.questionCard {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 16px;
}

.questionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.headerActions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.reorderControls {
  display: flex;
  gap: 6px;
}

.reorderButton {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  color: #66bdff;
  font-size: 14px;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  line-height: 1;
}

.reorderButton:hover:not(:disabled) {
  background: #f0f9ff;
  border-color: #4da3ff;
  color: #4da3ff;
}

.reorderButton:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  border-color: #eee;
  color: #aaa;
}

.questionNumber {
  font-size: 16px;
  font-weight: 600;
  color: #66bdff;
  font-family: "NanumSquareRoundEB", sans-serif;
}

.deleteButton {
  padding: 6px 16px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  color: #ff4444;
  font-size: 13px;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}

.deleteButton:hover {
  background: #fff5f5;
  border-color: #ff4444;
}

.deleteButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 옵션 관리 */
.optionRow {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}

.optionRow .textInput {
  flex: 1;
}

.removeOptionButton {
  width: 32px;
  height: 32px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  color: #999;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.removeOptionButton:hover {
  background: #fff5f5;
  border-color: #ff4444;
  color: #ff4444;
}

.removeOptionButton:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.addOptionButton {
  padding: 8px 16px;
  background: #fff;
  border: 1px dashed #66bdff;
  border-radius: 6px;
  color: #66bdff;
  font-size: 13px;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  margin-top: 8px;
}

.addOptionButton:hover {
  background: #f0f9ff;
}

.addOptionButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.addFieldButton {
  padding: 14px 24px;
  background: #fff;
  border: 2px dashed #66bdff;
  border-radius: 10px;
  color: #66bdff;
  font-size: 15px;
  font-family: "NanumSquareRoundEB", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  margin-top: 8px;
}

.addFieldButton:hover {
  background: #f0f9ff;
  border-color: #4da3ff;
}

.addFieldButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 옵션 설정 버튼 */
.enableOptionButton {
  padding: 12px 20px;
  background: #fff;
  border: 2px dashed #66bdff;
  border-radius: 8px;
  color: #66bdff;
  font-size: 14px;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  margin-top: 8px;
}

.enableOptionButton:hover {
  background: #f0f9ff;
  border-color: #4da3ff;
}

.enableOptionButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 옵션 구분선 */
.optionDivider {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0 16px 0;
  padding: 12px 0;
  border-top: 2px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
}

.optionDivider span {
  font-size: 14px;
  font-family: "NanumSquareRoundEB", sans-serif;
  color: #868e96;
}

/* 체크박스 선택 개수 제한 */
.limitInputGroup {
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
}

.limitInputItem {
  flex: 1;
}

.limitHint {
  margin: 0;
  padding: 10px 14px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 12px;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.5;
  border: 1px solid #e9ecef;
}

.removeOptionButton2 {
  padding: 6px 14px;
  background: white;
  border: 1px solid #ff6b6b;
  border-radius: 6px;
  color: #ff6b6b;
  font-size: 12px;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}

.removeOptionButton2:hover {
  background: #fff5f5;
  border-color: #ff4444;
  color: #ff4444;
}

.removeOptionButton2:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 폼 액션 버튼 */
.formActions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 40px;
  padding: 24px 0;
  background: transparent;
  border-top: 1px solid #e9ecef;
}

.saveButton {
  padding: 14px 32px;
  background: #66bdff;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 15px;
  font-family: "NanumSquareRoundEB", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}

.saveButton:hover {
  background: #4da3ff;
}

.saveButton:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.previewButton {
  padding: 14px 32px;
  background: white;
  border: 2px solid #66bdff;
  border-radius: 8px;
  color: #66bdff;
  font-size: 15px;
  font-family: "NanumSquareRoundEB", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}

.previewButton:hover {
  background: #f0f9ff;
  border-color: #4da3ff;
  color: #4da3ff;
}

.previewButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: #ddd;
  color: #999;
}

.closeBottomButton {
  padding: 14px 32px;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  color: #666;
  font-size: 15px;
  font-family: "NanumSquareRoundEB", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}

.closeBottomButton:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #cfcfcf;
  color: #333;
}

.closeBottomButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}


@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* 입금 정보 표시 */
.infoDisplay {
  background: transparent;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-top: 16px;
}

.infoRow {
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f5;
}

.infoRow:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.infoLabel {
  flex: 0 0 120px;
  font-weight: 600;
  color: #495057;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
}

.infoValue {
  flex: 1;
  color: #212529;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 14px;
}

/* 취소/환불 기본 규정 안내 */
.defaultPolicyNotice {
  padding: 16px 18px;
  background: #f0f9ff;
  border: 1px solid #c8e6ff;
  border-radius: 10px;
  margin-bottom: 16px;
}

.defaultPolicyHeader {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.defaultPolicyBadge {
  display: inline-block;
  padding: 2px 8px;
  background: #66bdff;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 20px;
  font-family: "NanumSquareRoundB", sans-serif;
  letter-spacing: 0.3px;
}

.defaultPolicyLabel {
  font-size: 12px;
  color: #5a8fa8;
  font-family: "NanumSquareRoundR", sans-serif;
}

.defaultPolicyList {
  margin: 0 0 10px 0;
  padding-left: 18px;
  list-style: disc;
}

.defaultPolicyList li {
  font-size: 13px;
  color: #2d5f7a;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.8;
}

.defaultPolicyNote {
  margin: 0;
  font-size: 12px;
  color: #7a9fb5;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.5;
}

.toggleOptional {
  font-size: 12px;
  color: #999;
  font-weight: 400;
  font-family: "NanumSquareRoundR", sans-serif;
}

/* 취소/환불 규정 토글 */
.cancelPolicyToggle {
  margin-bottom: 20px;
}

.toggleLabel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  padding: 14px 18px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
}

.toggleText {
  font-size: 15px;
  font-weight: 500;
  color: #444;
  font-family: "NanumSquareRoundB", sans-serif;
}

.toggleSwitch {
  width: 46px;
  height: 26px;
  background: #ddd;
  border-radius: 13px;
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
}

.toggleSwitchOn {
  background: #66bdff;
}

.toggleKnob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  transition: left 0.2s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.toggleSwitchOn .toggleKnob {
  left: 23px;
}

.cancelPolicyContent {
  animation: fadeIn 0.15s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 이미지 업로드 */
.imageUploadArea {
  width: 100%;
}

.imageGridArea {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
}

.imageUploadBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  border: 2px dashed #ddd;
  border-radius: 10px;
  cursor: pointer;
  color: #999;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: border-color 0.2s, color 0.2s;
  background: #fafafa;
  box-sizing: border-box;
}

.imageUploadBtn:hover {
  border-color: #66bdff;
  color: #66bdff;
}

.imageUploadBtnDisabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.imagePreviewWrapper {
  position: relative;
  display: inline-block;
  width: 120px;
  height: 120px;
  border-radius: 10px;
  overflow: hidden;
}

.imagePreview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 10px;
}

.imageOverlayActions {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s;
  border-radius: 10px;
}

.imagePreviewWrapper:hover .imageOverlayActions {
  opacity: 1;
}

.imageChangeBtn {
  padding: 5px 10px;
  background: #fff;
  border-radius: 6px;
  font-size: 12px;
  color: #333;
  cursor: pointer;
  font-family: "NanumSquareRoundB", sans-serif;
  transition: background 0.15s;
}

.imageChangeBtn:hover {
  background: #f0f9ff;
}

.imageRemoveBtn {
  padding: 5px 10px;
  background: #fff;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  color: #e53935;
  cursor: pointer;
  font-family: "NanumSquareRoundB", sans-serif;
  transition: background 0.15s;
}

.imageRemoveBtn:hover {
  background: #fff5f5;
}

.imageRemoveBtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 모바일 반응형 */
@media (max-width: 480px) {
  .section {
    margin-bottom: 16px;
  }

  .sectionTitle {
    font-size: 18px;
  }

  .questionCard {
    padding: 16px;
  }

  .radio {
    gap: 6px;
  }

  .radio label {
    font-size: 13px;
    padding: 8px 16px;
    min-width: 70px;
  }

  .formActions {
    flex-direction: column;
    padding: 16px;
  }

  .saveButton {
    width: 100%;
  }

  .previewButton {
    width: 100%;
  }

  .closeBottomButton {
    width: 100%;
  }

  .limitInputGroup {
    flex-direction: column;
    gap: 0;
  }

  .paper {
    padding: 14px;
    box-shadow: none;
  }

  .closeButton {
    top: 10px;
    right: 10px;
    font-size: 22px;
    width: 32px;
    height: 32px;
  }

  .logo {
    width: 55%;
    max-width: 200px;
  }

  .title {
    font-size: 14px;
    margin: 4px;
  }

  .inputs {
    width: 98%;
  }
}
</style>
